import type { GenerationsStepId } from "./steps";

import {
  PROCESSING_STATE_COLLECTION_PROPAGATED,
  PROPAGATION_STATE,
  PUBLISH_STATUS,
  type Propagation,
  type PublishProgress,
} from "@/types/propagation";

export type PropagationStatusColor =
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "success"
  | "warning";

export interface PropagationStatusBadgeConfig {
  label: string;
  color: PropagationStatusColor;
}

function humanizeStatus(status: string): string {
  const stripped = status.replace(/^PROPAGATION_STATE_/, "");
  const lower = stripped.toLowerCase().replace(/_/g, " ");
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

const STATUS_BADGES: Record<string, PropagationStatusBadgeConfig> = {
  [PROPAGATION_STATE.UNSPECIFIED]: { label: "Unspecified", color: "default" },
  [PROPAGATION_STATE.SETUP]: { label: "Setup", color: "default" },
  [PROPAGATION_STATE.PILOT_GENERATING]: {
    label: "Pilot generating",
    color: "info",
  },
  [PROPAGATION_STATE.PILOT_READY]: { label: "Pilot ready", color: "info" },
  [PROPAGATION_STATE.PILOT_ACCEPTED]: {
    label: "Pilot accepted",
    color: "primary",
  },
  [PROPAGATION_STATE.PROPAGATING]: { label: "Propagating", color: "warning" },
  [PROPAGATION_STATE.COLLECTION_REVIEW]: {
    label: "Collection review",
    color: "warning",
  },
  [PROPAGATION_STATE.COLLECTION_ACCEPTED]: {
    label: "Collection accepted",
    color: "primary",
  },
  [PROPAGATION_STATE.PUBLISHED]: { label: "Published", color: "success" },
  [PROPAGATION_STATE.SCHEDULED]: { label: "Scheduled", color: "info" },
  [PROPAGATION_STATE.FAILED]: { label: "Failed", color: "error" },
  [PROPAGATION_STATE.ARCHIVED]: { label: "Archived", color: "default" },
};

export function propagationStatusBadge(
  status: string,
): PropagationStatusBadgeConfig {
  // `Object.hasOwn` (not a bare lookup) so inherited keys like "toString" or
  // "constructor" — `status` is a loose `z.string()` — don't resolve to a
  // `Object.prototype` member and slip past the `??` fallback.
  return Object.hasOwn(STATUS_BADGES, status)
    ? STATUS_BADGES[status]
    : { label: humanizeStatus(status), color: "default" };
}

export const PROPAGATION_STATE_PROPAGATE_COLLECTION =
  "PROPAGATION_STATE_PROPAGATE_COLLECTION";
export const PROPAGATION_STATE_PUBLISH = PROPAGATION_STATE.PUBLISHED;

export interface PublishOutcome {
  /** Assets whose last reported status is PUBLISHED. */
  publishedSlugs: string[];
  /** Inline error, or null when every expected asset published. */
  error: string | null;
}

/**
 * Decides a publish run from its frames. An asset counts by its last frame and
 * only if it was sent; a slug-less FAILED frame is a run-level failure whose
 * reason is kept, while the slug-less opening status frame is ignored.
 */
export function publishOutcome(
  frames: PublishProgress[],
  expectedSlugs: readonly string[],
): PublishOutcome {
  const lastByAsset = new Map<string, PublishProgress>();
  const runFailures: PublishProgress[] = [];
  for (const frame of frames) {
    if (frame.asset_slug) lastByAsset.set(frame.asset_slug, frame);
    else if (frame.status === PUBLISH_STATUS.FAILED) runFailures.push(frame);
  }
  const publishedSlugs = [...lastByAsset.values()]
    .filter((frame) => frame.status === PUBLISH_STATUS.PUBLISHED)
    .map((frame) => frame.asset_slug);
  const published = new Set(publishedSlugs);
  const unpublished = expectedSlugs.filter((slug) => !published.has(slug));
  if (unpublished.length === 0) return { publishedSlugs, error: null };

  const failedFrames = [
    ...runFailures,
    ...unpublished.flatMap((slug) => lastByAsset.get(slug) ?? []),
  ];
  const reasons = [
    ...new Set(failedFrames.map((frame) => frame.failure_reason.trim())),
  ].filter(Boolean);
  if (lastByAsset.size === 0 && reasons.length === 0) {
    return {
      publishedSlugs,
      error: "Publishing didn't finish. Please try again.",
    };
  }
  const noun = expectedSlugs.length === 1 ? "asset" : "assets";
  const detail = reasons.length > 0 ? `: ${reasons.join("; ")}` : "";
  return {
    publishedSlugs,
    error: `${unpublished.length} of ${expectedSlugs.length} ${noun} didn't publish${detail}. Please try again.`,
  };
}

export const COLLECTION_POLL_INTERVAL_MS = 5_000;

export function isCollectionPropagating(propagation?: Propagation): boolean {
  return (
    propagation !== undefined && !isCollectionPropagationSettled(propagation)
  );
}

export function isCollectionPropagationSettled(
  propagation?: Propagation,
): boolean {
  return (
    propagation?.processing_state === PROCESSING_STATE_COLLECTION_PROPAGATED ||
    propagation?.propagation_state === PROPAGATION_STATE.PUBLISHED ||
    propagation?.propagation_state === PROPAGATION_STATE.FAILED ||
    propagation?.propagation_state === PROPAGATION_STATE.ARCHIVED
  );
}

export const STORY_FILTER_STATUSES = [
  PROPAGATION_STATE.SETUP,
  PROPAGATION_STATE.PILOT_GENERATING,
  PROPAGATION_STATE.PILOT_READY,
  PROPAGATION_STATE.PILOT_ACCEPTED,
  PROPAGATION_STATE.PROPAGATING,
  PROPAGATION_STATE.COLLECTION_REVIEW,
  PROPAGATION_STATE.COLLECTION_ACCEPTED,
  PROPAGATION_STATE.PUBLISHED,
] as const;

/** Blueprint name lookup keyed by blueprint slug (see `useBlueprints`). */
export type BlueprintNamesBySlug = ReadonlyMap<string, string>;

/** Slug of the blueprint the propagation runs; `blueprint_id` wins over the origin. */
export function propagationBlueprintSlug(
  propagation: Propagation,
): string | undefined {
  return (
    propagation.blueprint_id?.trim() ||
    propagation.from_blueprint_id?.trim() ||
    undefined
  );
}

const NAME_SEPARATOR = " · ";

/** "<group handle> · <blueprint name>", dropping whichever half is blank. */
export function composePropagationName(
  groupHandle?: string,
  blueprintName?: string,
): string {
  return [groupHandle?.trim(), blueprintName?.trim()]
    .filter(Boolean)
    .join(NAME_SEPARATOR);
}

/**
 * Display name for a propagation. New propagations carry `name` from creation;
 * older rows are composed from the group handle plus a resolvable blueprint
 * name, and with neither we fall back to the slug (the only identifier the
 * backend always ships).
 */
export function getPropagationName(
  propagation: Propagation,
  blueprintNames?: BlueprintNamesBySlug,
): string {
  const stored = propagation.name?.trim();
  if (stored) return stored;
  const blueprintSlug = propagationBlueprintSlug(propagation);
  const blueprintName = blueprintSlug
    ? blueprintNames?.get(blueprintSlug)
    : undefined;
  return (
    composePropagationName(propagation.group_id, blueprintName) ||
    propagation.slug.trim()
  );
}

/**
 * Handle of the group the propagation targets, read from the name's first half
 * ("<handle> · <blueprint>"). `group_id` is the numeric catalog id, never a
 * handle, so a row without a name yields no handle rather than a wrong filter.
 */
export function propagationGroupHandle(
  propagation: Propagation,
): string | undefined {
  return propagation.name?.split(NAME_SEPARATOR)[0]?.trim() || undefined;
}

export function getPropagationItemCount(propagation: Propagation): number {
  return propagation.items.length;
}

// Which flow step a row opens, by workflow state. Pilot states land on the
// setup canvas (which owns "Run generation" and "Accept Pilot"); anything past
// acceptance lands on the collection review. Published rows aren't editable.
const STEP_ID_BY_PROPAGATION_STATE: Record<string, GenerationsStepId> = {
  [PROPAGATION_STATE.UNSPECIFIED]: "setup",
  [PROPAGATION_STATE.SETUP]: "setup",
  [PROPAGATION_STATE.PILOT_GENERATING]: "setup",
  [PROPAGATION_STATE.PILOT_READY]: "setup",
  [PROPAGATION_STATE.PILOT_ACCEPTED]: "setup",
  [PROPAGATION_STATE.FAILED]: "setup",
  [PROPAGATION_STATE.PROPAGATING]: "accept-collection",
  [PROPAGATION_STATE.COLLECTION_REVIEW]: "accept-collection",
  [PROPAGATION_STATE.COLLECTION_ACCEPTED]: "accept-collection",
  [PROPAGATION_STATE.SCHEDULED]: "accept-collection",
};

export function propagationEditStepId(
  propagation: Propagation,
): GenerationsStepId | undefined {
  const state = propagation.propagation_state;
  if (state === PROPAGATION_STATE.PUBLISHED) return undefined;
  // `Object.hasOwn` guards against inherited keys ("constructor", …): the state
  // is a loose `z.string()` and a bare lookup would return a prototype function.
  return Object.hasOwn(STEP_ID_BY_PROPAGATION_STATE, state)
    ? STEP_ID_BY_PROPAGATION_STATE[state]
    : "setup";
}

export const PROPAGATIONS_PAGE_SIZE = 20;

/**
 * Rows fetched from the backend in one request. The list endpoint has no name
 * search, so we fetch a full page up front and filter/sort/paginate the result
 * client-side (see {@link selectPropagationsPage}). Passing an explicit high
 * limit — rather than relying on the backend's default page size — keeps rows
 * from being silently unreachable. This is a temporary ceiling until the
 * backend supports server-side name search + pagination.
 */
export const PROPAGATIONS_FETCH_LIMIT = 500;

/** Shared collator for case-insensitive name sorting (see selectPropagationsPage). */
const NAME_COLLATOR = new Intl.Collator(undefined, { sensitivity: "base" });

export type PropagationSortDir = "asc" | "desc";

export interface PropagationsListSearch {
  q?: string;
  status?: string;
  page?: number;
  sort?: PropagationSortDir;
}

export interface SelectPropagationsPageParams {
  search?: string;
  status?: string;
  sortDir?: PropagationSortDir;
  page?: number;
  pageSize?: number;
  /** Resolves blueprint slugs to names so search/sort use the display name. */
  blueprintNames?: BlueprintNamesBySlug;
}

export interface PropagationsPageView {
  items: Propagation[];
  total: number;
  page: number;
  pageCount: number;
}

/**
 * Pure client-side filter → sort → paginate over the full propagation list.
 * The backend list endpoint has no name/search support, so searching and
 * sorting by name are done here; kept pure so it can be unit-tested directly.
 */
export function selectPropagationsPage(
  all: Propagation[],
  {
    search = "",
    status,
    sortDir = "asc",
    page = 1,
    pageSize = PROPAGATIONS_PAGE_SIZE,
    blueprintNames,
  }: SelectPropagationsPageParams = {},
): PropagationsPageView {
  const query = search.trim().toLowerCase();

  // Precompute the display name once per row (it's read for filter + sort), then
  // sort with a single shared collator instead of re-deriving collation rules on
  // every `localeCompare`. `sensitivity: "base"` makes the case-insensitive
  // ordering explicit rather than ICU-default-dependent.
  const keyed = all
    .map((propagation) => ({
      propagation,
      name: getPropagationName(propagation, blueprintNames),
    }))
    .filter(({ propagation, name }) => {
      if (status && propagation.propagation_state !== status) return false;
      if (query && !name.toLowerCase().includes(query)) return false;
      return true;
    });

  keyed.sort((a, b) => NAME_COLLATOR.compare(a.name, b.name));
  const ordered = sortDir === "asc" ? keyed : keyed.reverse();
  const sorted = ordered.map(({ propagation }) => propagation);

  const total = sorted.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const clampedPage = Math.min(Math.max(1, page), pageCount);
  const start = (clampedPage - 1) * pageSize;
  const items = sorted.slice(start, start + pageSize);

  return { items, total, page: clampedPage, pageCount };
}
